import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import apolloClient from 'api/apolloClient';
import {
  GET_CART,
  CAN_COMBINE_WITH,
  ADD_DYNAMIC_PORTFOLIO,
  REMOVE_PORTFOLIO_FROM_CART,
  SAVE_PREMIUM_COST_ANSWERS,
  SAVE_PROPOSAL,
  CREATE_CART,
  COPY_CART,
  UPDATE_BC,
  UPDATE_CART_NAME,
  CONFIRM_CART_ITEMS,
  APPLY_BROKER_CART,
  GET_ALL_BROKER_CARTS,
  GET_AVAILABLE_BROKER_CARTS,
} from 'api/queries/cartServiceV2';
import { BrokerCartInput, Cart, CombinationType, CreateCartInput, PortfolioProductsMap } from 'types/cartServiceV2';
import { Primitive } from 'types/general';

export async function getCart(cartId: string): Promise<Cart> {
  return (
    await apolloClient.query<{ getCart: Cart }>({
      query: GET_CART,
      fetchPolicy: 'no-cache',
      variables: { id: cartId },
    })
  ).data.getCart;
}

export async function getProductsAvailableForCombining(
  cartId: string,
  productLineId: ProductLineId,
  inPortfolioCartItemIds: string[],
  combinationType: CombinationType,
): Promise<ApolloQueryResult<{ canCombineWith: PortfolioProductsMap }>> {
  return apolloClient.query<{ canCombineWith: PortfolioProductsMap }>({
    query: CAN_COMBINE_WITH,
    fetchPolicy: 'no-cache',
    variables: { cartId, productLineId, inPortfolioCartItemIds, combinationType },
  });
}

export async function addDynamicPortfolio(
  cartId: string,
  portfolioId: string,
  name: string,
  cartItemIds: string[],
  // true - for Create Portfolio step(RFQ quoting flow) if browsing type is RFQ
  simplifiedBC: boolean,
): Promise<FetchResult<{ addDynamicPortfolio: Cart }>> {
  return apolloClient.mutate<{ addDynamicPortfolio: Cart }>({
    mutation: ADD_DYNAMIC_PORTFOLIO,
    variables: { cartId, portfolioId, name, cartItemIds, simplifiedBC },
  });
}

export async function removePortfolioFromCart(
  cartId: string,
  portfolioId: string,
  isLast: boolean,
): Promise<FetchResult<{ removePortfolioConfigFromCart: Cart }>> {
  return apolloClient.mutate<{ removePortfolioConfigFromCart: Cart }>({
    mutation: REMOVE_PORTFOLIO_FROM_CART,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioId, removeLastActiveBC: isLast },
  });
}

export async function savePremiumCostAnswers(
  cartId: string,
  premiumCostAnswers: Record<string, Primitive>,
): Promise<FetchResult<{ savePremiumCostAnswers: Cart }>> {
  return apolloClient.mutate<{ savePremiumCostAnswers: Cart }>({
    mutation: SAVE_PREMIUM_COST_ANSWERS,
    variables: { cartId, premiumCostAnswers },
  });
}

export async function saveProposal(cartId: string, cartName: string): Promise<FetchResult<{ saveProposal: Cart }>> {
  return apolloClient.mutate<{ saveProposal: Cart }>({
    mutation: SAVE_PROPOSAL,
    fetchPolicy: 'no-cache',
    variables: { cartId, cartName },
  });
}

export async function createCart(cartInput: CreateCartInput): Promise<Cart> {
  const res = await apolloClient.mutate<{ createCart: Cart }>({
    mutation: CREATE_CART,
    fetchPolicy: 'no-cache',
    variables: { ...cartInput, cartSource: cartInput.cartSource || 'AGENT' },
  });
  return res.data!.createCart;
}

export async function copyCart(cartId: string, cartName = ''): Promise<Cart> {
  const res = await apolloClient.mutate<{ copyCart: Cart }>({
    mutation: COPY_CART,
    fetchPolicy: 'no-cache',
    variables: { id: cartId, name: cartName },
  });
  return res.data!.copyCart;
}

export async function updateLastBC(cartId: string, browsingCriteria: CartBrowsingCriteria): Promise<Cart> {
  const res = await apolloClient.mutate<{ updateLastBC: Cart }>({
    mutation: UPDATE_BC,
    fetchPolicy: 'no-cache',
    variables: { cartId, browsingCriteria },
  });
  return res.data!.updateLastBC;
}

export async function updateCartName(cartId: string, cartName: string): Promise<Cart> {
  const res = await apolloClient.mutate<{ updateCartName: Cart }>({
    mutation: UPDATE_CART_NAME,
    fetchPolicy: 'no-cache',
    variables: { id: cartId, name: cartName },
  });
  return res.data!.updateCartName;
}

export async function confirmCartItems(
  cartId: string,
  portfolioIdToSelectIndex: Record<string, number>,
): Promise<Cart> {
  const res = await apolloClient.mutate<{ confirmCartItems: Cart }>({
    mutation: CONFIRM_CART_ITEMS,
    fetchPolicy: 'no-cache',
    variables: { cartId, portfolioIdToSelectIndex },
  });

  return res.data!.confirmCartItems;
}

export async function getAllBrokerCarts(): Promise<Cart[]> {
  const res = await apolloClient.query<{ getAllBrokerCarts: Cart[] }>({
    query: GET_ALL_BROKER_CARTS,
  });
  return res.data?.getAllBrokerCarts ?? [];
}

export async function getAvailableBrokerCarts(input: BrokerCartInput): Promise<Cart[]> {
  const res = await apolloClient.query<{ getAvailableBrokerCarts: Cart[] }>({
    query: GET_AVAILABLE_BROKER_CARTS,
    variables: { request: input },
  });
  return res.data?.getAvailableBrokerCarts ?? [];
}

export async function applyBrokerCart(cartId: string, bc: BrokerCartInput, groupId: string, name = ''): Promise<Cart> {
  const res = await apolloClient.mutate<{ applyBrokerCart: Cart }>({
    mutation: APPLY_BROKER_CART,
    fetchPolicy: 'no-cache',
    variables: { cartId, bc, groupId, name },
  });

  return res.data!.applyBrokerCart;
}
